/**
 * iOS PWA-specific handling for push notification navigation
 * iOS PWAs have limitations with service worker navigation, so we use postMessage as fallback
 */

interface NotificationClickMessage {
    type: 'NOTIFICATION_CLICK';
    url: string;
    taskId?: string | number;
    timestamp: string;
}

export class IOSPWAHandler {
    private static instance: IOSPWAHandler;
    private messageListener: ((event: MessageEvent) => void) | null = null;

    private constructor() {
    }

    public static getInstance(): IOSPWAHandler {
        if (!IOSPWAHandler.instance) {
            IOSPWAHandler.instance = new IOSPWAHandler();
        }
        return IOSPWAHandler.instance;
    }

    public static isIOSDevice(): boolean {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    }

    public static isIOSPWA(): boolean {
        return IOSPWAHandler.isIOSDevice() && (window.navigator as any).standalone === true;
    }

    public initialize(): void {
        if (IOSPWAHandler.isIOSPWA()) {
            this.setupMessageListener();
        }
    }

    private setupMessageListener(): void {
        if (this.messageListener) {
            return;
        }

        this.messageListener = (event: MessageEvent) => {
            if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
                this.handleNotificationClick(event.data as NotificationClickMessage);
            }
        };

        navigator.serviceWorker.addEventListener('message', this.messageListener);
    }

    private handleNotificationClick(message: NotificationClickMessage): void {
        const {url, taskId, timestamp} = message;

        try {
            // Construct the URL with task parameter if needed
            let finalUrl = url;
            if (taskId && !url.includes('?task=')) {
                finalUrl = url.includes('?')
                    ? `${url}&task=${taskId}`
                    : `${url}?task=${taskId}`;
            }

            // For iOS PWA, we need to use window.location instead of history API
            if (window.location.pathname !== new URL(finalUrl, window.location.origin).pathname ||
                window.location.search !== new URL(finalUrl, window.location.origin).search) {
                window.location.href = finalUrl;
            } else {
                // If we're already on the right page, trigger a refresh to ensure task opens
                window.location.reload();
            }

        } catch (error) {
            console.error('[DEBUG iOS PWA] Error handling notification click:', error);

            // Fallback: try simple navigation
            try {
                window.location.href = url;
            } catch (fallbackError) {
                console.error('[DEBUG iOS PWA] Fallback navigation also failed:', fallbackError);
            }
        }
    }

    public cleanup(): void {
        if (this.messageListener) {
            navigator.serviceWorker.removeEventListener('message', this.messageListener);
            this.messageListener = null;
        }
    }
}

// Auto-initialize when module is imported
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    // Wait for page load to ensure service worker is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            IOSPWAHandler.getInstance().initialize();
        });
    } else {
        IOSPWAHandler.getInstance().initialize();
    }
}
